/**
 * 照らす レポート パターン生成（DEV/デモ専用）。
 * 週報: 4指標 × 4天気 の全256通り（＋代表サブセット）。
 * 月報: 4週の推移アーキタイプ（全指標一律6 ＋ 1指標だけ悪化4 ＝ 10）。
 */
import type { TerasuWeather } from './generateDummyTerasuReport';

const IND = ['direction', 'drive', 'connection', 'capacity'] as const;
const INDJP: Record<string, string> = {
  direction: '方向',
  drive: '推進',
  connection: 'つながり',
  capacity: '余力',
};
const WJP: Record<TerasuWeather, string> = { sunny: '晴', cloudy: '曇', rain: '雨', storm: '雷' };
const WCODE: Record<TerasuWeather, string> = { sunny: 'S', cloudy: 'C', rain: 'R', storm: 'Z' };
const WEATHERS: TerasuWeather[] = ['sunny', 'cloudy', 'rain', 'storm'];

export interface WeeklyPattern {
  code: string;
  label: string;
  weekWeather: Record<string, TerasuWeather>;
}

/** 週報 全256通り */
export function weeklyAllPatterns(): WeeklyPattern[] {
  const out: WeeklyPattern[] = [];
  for (const a of WEATHERS)
    for (const b of WEATHERS)
      for (const c of WEATHERS)
        for (const d of WEATHERS) {
          const arr: TerasuWeather[] = [a, b, c, d];
          out.push({
            code: arr.map((w) => WCODE[w]).join(''),
            label: IND.map((k, i) => `${INDJP[k]}${WJP[arr[i]]}`).join('・'),
            weekWeather: { direction: a, drive: b, connection: c, capacity: d },
          });
        }
  return out;
}

/** 週報 代表サブセット（一律4段階＋1指標だけ悪化4＋混在数種） */
export function weeklyRepresentative(): WeeklyPattern[] {
  const all = weeklyAllPatterns();
  const pick = (code: string) => all.find((p) => p.code === code)!;
  return [
    pick('SSSS'), pick('CCCC'), pick('RRRR'), pick('ZZZZ'), // 一律
    pick('SSSR'), pick('SSRS'), pick('SRSS'), pick('RSSS'), // 1指標だけ雨
    pick('SSSZ'), pick('SSCZ'), pick('SCRZ'), pick('SCCR'), // 混在
  ];
}

export interface MonthlyPattern {
  code: string;
  label: string;
  monthGrid: Record<string, TerasuWeather[]>;
}

const TRAJ: Record<string, TerasuWeather[]> = {
  好調維持: ['sunny', 'sunny', 'sunny', 'sunny'],
  悪化: ['sunny', 'cloudy', 'rain', 'rain'],
  改善: ['rain', 'cloudy', 'sunny', 'sunny'],
  V字回復: ['sunny', 'rain', 'rain', 'sunny'],
  不調維持: ['rain', 'rain', 'rain', 'rain'],
  急落: ['sunny', 'sunny', 'cloudy', 'storm'],
};

/** 月報 アーキタイプ（全指標一律6 ＋ 1指標だけ〈悪化/改善/急落/V字〉各4 ＝ 22） */
export function monthlyAllPatterns(): MonthlyPattern[] {
  const out: MonthlyPattern[] = [];
  // (1) 全指標一律 6種
  for (const [name, tr] of Object.entries(TRAJ)) {
    const grid: Record<string, TerasuWeather[]> = {};
    for (const k of IND) grid[k] = [...tr];
    out.push({ code: `ALL_${name}`, label: `全指標 ${name}`, monthGrid: grid });
  }
  // (2) 1指標だけ〈○○〉: base を敷き、target 指標だけ別推移
  const families: { tag: string; base: TerasuWeather[]; target: TerasuWeather[] }[] = [
    { tag: '悪化', base: TRAJ['好調維持'], target: TRAJ['悪化'] },
    { tag: '改善', base: TRAJ['不調維持'], target: TRAJ['改善'] },
    { tag: '急落', base: TRAJ['好調維持'], target: TRAJ['急落'] },
    { tag: 'V字回復', base: TRAJ['好調維持'], target: TRAJ['V字回復'] },
  ];
  for (const fam of families) {
    for (const target of IND) {
      const grid: Record<string, TerasuWeather[]> = {};
      for (const k of IND) grid[k] = [...fam.base];
      grid[target] = [...fam.target];
      out.push({
        code: `${target}_${fam.tag}`,
        label: `${INDJP[target]}だけ${fam.tag}`,
        monthGrid: grid,
      });
    }
  }
  return out;
}
